import { createSlice } from '@reduxjs/toolkit';

import { ApiStatus, SortDirection, StatusTypes } from 'const';

import {
  CHANGE_USER_ROLE,
  CREATE_ACCOUNT,
  DELETE_ACCOUNT,
  FETCH_CSV_HISTORY,
  GET_ALL_ACCOUNTS,
  GET_COMPANIES_LIST,
  GET_ROW_ID_STATUS,
  POST_CREATE_ACCOUNTS_CSV,
  RESEND_EMAIL,
  UPDATE_ACCOUNT
} from 'redux/sagas/actions/people';

const initialState = {
  pageNo: 0,
  pageSize: 10,
  searchPattern: '',
  isLoading: false,
  historyList: [],
  sortBy: 'updatedOn',
  sortDirection: SortDirection.ASC,
  networkError: false,
  companies: [],
  isLoadingCreation: false,
  isLoadingCreationBulk: false
};

const setWebUsersData = (state, action) => {
  const {
    pageNo,
    pageSize,
    totalPagesAvailable,
    totalRecordsAcrossAllPages,
    webUserList
  } = action.payload;
  state = {
    ...state,
    peopleList: webUserList,
    pageNo,
    pageSize,
    totalRecords: totalRecordsAcrossAllPages,
    totalPagesAvailable,
    networkError: false
  };
  return state;
};

const setCreateAccountsCSVResponse = (state, action) => {
  const {
    createdCount,
    failedCount,
    skippedCount,
    totalCount
  } = action.payload;
  state = {
    ...state,
    createdCount,
    failedCount,
    skippedCount,
    totalCount,
    postCSVSuccess: true,
    isLoadingCreation: false,
    isLoadingCreationBulk: false
  };
  return state;
};

const setUpateAccountData = (state, action) => {
  const data = action.payload;
  const list = [...state.peopleList];
  try {
    if (data.length) {
      data.forEach(row => {
        const selectedIndex = list.findIndex(
          item => item.email === row.cognitoId
        );
        list[selectedIndex] = {
          ...list[selectedIndex],
          status:
            row.updateSuccessful &&
            list[selectedIndex].status === StatusTypes.DISABLED
              ? StatusTypes.ENABLED
              : StatusTypes.DISABLED,
          action_progress: row.updateSuccessful
        };
      });
    }

    return {
      ...state,
      peopleList: list
    };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(err);
    return {
      ...state,
      peopleList: list
    };
  }
};

const setErrorNotification = (state, action) => {
  const { message } = action?.payload || {};
  return {
    ...state,
    message,
    status: ApiStatus.error,
    targetRole: '',
    isLoadingCreation: false,
    isLoadingCreationBulk: false
  };
};

const blankNotification = state => ({
  ...state,
  message: null,
  status: null,
  isLoadingCreation: true
});

const setSuccessNotification = (state, action) => {
  const { message } = action?.payload || {};
  return {
    ...state,
    message,
    status: ApiStatus.success,
    targetRole: ''
  };
};

export const peopleSlice = createSlice({
  name: '@@people',
  initialState,
  reducers: {
    CLEAR_NOTIFICATION: blankNotification,
    SET_SELECTED_ROLES: (state, action) => ({
      ...state,
      selectedRoles: action.payload
    }),
    SET_SEARCH_PATTERN: (state, action) => ({
      ...state,
      searchPattern: action.payload
    }),
    RESET_STATE: () => ({ ...initialState }),
    SHOW_SPINNER: state => ({
      ...state,
      isLoading: true
    }),
    HIDE_SPINNER: state => ({
      ...state,
      isLoading: false
    }),
    FILE_UPLOAD_START: state => ({
      ...state,
      isUploading: true
    }),
    FILE_UPLOAD_COMPLETE: state => ({
      ...state,
      isUploading: false
    }),
    DISABLE_POST_CSV_SUCCESS_FLAG: state => ({
      ...state,
      postCSVSuccess: false
    }),
    SET_CHANGED_ROLE: (state, action) => ({
      ...state,
      targetRole: action.payload
    }),
    SET_SELECTED_STATUS_OPTIONS: (state, action) => ({
      ...state,
      selectedStatusOptions: action.payload
    }),
    SET_SORT_DATA: (state, action) => {
      const { sortBy } = action.payload;
      return {
        ...state,
        sortBy,
        sortDirection:
          state.sortDirection === SortDirection.ASC
            ? SortDirection.DESC
            : SortDirection.ASC
      };
    },
    CLOSE_MENU: (state, action) => ({
      ...state,
      isMenuOpen: action.payload
    })
  },
  extraReducers: {
    [CREATE_ACCOUNT._SUCCESS]: state => ({
      ...state,
      status: ApiStatus.success,
      isLoadingCreation: false
    }),
    [GET_ALL_ACCOUNTS._SUCCESS]: setWebUsersData,
    [GET_ALL_ACCOUNTS._ERROR]: state => ({
      ...state,
      searchPattern: state.searchPattern,
      networkError: true
    }),
    [UPDATE_ACCOUNT._SUCCESS]: setUpateAccountData,
    [FETCH_CSV_HISTORY._SUCCESS]: (state, action) => ({
      ...state,
      historyList: action.payload
    }),
    [POST_CREATE_ACCOUNTS_CSV._REQUEST]: state => ({
      ...state,
      isLoadingCreationBulk: true
    }),
    [POST_CREATE_ACCOUNTS_CSV._SUCCESS]: setCreateAccountsCSVResponse,
    [POST_CREATE_ACCOUNTS_CSV._ERROR]: setErrorNotification,
    [CREATE_ACCOUNT._REQUEST]: blankNotification,
    [CREATE_ACCOUNT._ERROR]: setErrorNotification,
    [GET_ROW_ID_STATUS._SUCCESS]: (state, action) => ({
      ...state,
      fileDataList: action.payload
    }),
    [DELETE_ACCOUNT._REQUEST]: blankNotification,
    [CHANGE_USER_ROLE._SUCCESS]: setSuccessNotification,
    [CHANGE_USER_ROLE._ERROR]: setErrorNotification,
    [DELETE_ACCOUNT._SUCCESS]: setSuccessNotification,
    [DELETE_ACCOUNT._ERROR]: setErrorNotification,
    [RESEND_EMAIL._SUCCESS]: setSuccessNotification,
    [RESEND_EMAIL._ERROR]: setErrorNotification,
    [GET_COMPANIES_LIST._SUCCESS]: (state, action) => ({
      ...state,
      companies: action.payload
    })
  }
});

export const {
  CLEAR_NOTIFICATION,
  SET_SELECTED_ROLES,
  SET_SEARCH_PATTERN,
  RESET_STATE,
  SHOW_SPINNER,
  HIDE_SPINNER,
  FILE_UPLOAD_START,
  FILE_UPLOAD_COMPLETE,
  DISABLE_POST_CSV_SUCCESS_FLAG,
  SET_CHANGED_ROLE,
  SET_SELECTED_STATUS_OPTIONS,
  SET_SORT_DATA,
  CLOSE_MENU
} = peopleSlice.actions;

export default peopleSlice.reducer;
